'use client';

import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyCoverThumb } from '@/features/cards';
import { cn } from '@/shared/utils/formatting';
import { ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { CopyrightYear } from '@/components/ui/copyright-year';

import type { ContextualSidebarShellProps } from './types';

/**
 * Visual primitive for any context-scoped sidebar. Owns layout only —
 * data fetching belongs to the container component (e.g.
 * `CourseSidebarContent`). The footer + chrome are intentionally identical
 * to the channel-scoped sidebar so the user feels continuity when the
 * sidebar morphs on navigation.
 */
export function ContextualSidebarShell({
  backHref,
  backLabel,
  entity,
  navItems,
}: ContextualSidebarShellProps) {
  const pathname = usePathname();
  const { open } = useSidebar();

  return (
    <>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={backLabel}>
                  <Link href={backHref} className="mx-auto">
                    <ArrowLeft />
                    <span>{backLabel}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {open ? <EntityHeader entity={entity} /> : null}

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const isActive = !item.disabled && pathname.startsWith(item.href);
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.id}>
                    {item.disabled ? (
                      <SidebarMenuButton
                        tooltip={item.label}
                        className={cn('opacity-50 cursor-not-allowed')}
                        disabled
                      >
                        <Icon />
                        <span>{item.label}</span>
                        {item.badge ? <SidebarMenuBadge>{item.badge}</SidebarMenuBadge> : null}
                      </SidebarMenuButton>
                    ) : (
                      <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
                        <Link href={item.href} className="mx-auto">
                          <Icon className={isActive ? 'text-red-primary' : ''} />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    )}
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator className="mt-auto" />
      </SidebarContent>

      <SidebarFooter>
        {open ? (
          <div className="px-3 py-3 text-2xs font-medium text-sidebar-foreground/50 uppercase tracking-widest">
            <div className="flex gap-3 mb-2">
              <Link
                href="https://taboo.tv/terms-conditions"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sidebar-foreground/80 transition-colors"
              >
                Terms
              </Link>
              <Link
                href="https://taboo.tv/contact-us"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-sidebar-foreground/80 transition-colors"
              >
                Help
              </Link>
            </div>
            <p>
              &copy; <CopyrightYear /> Taboo TV Studios LLC
            </p>
          </div>
        ) : null}
      </SidebarFooter>
    </>
  );
}

/**
 * Course-context entity card. Mirrors `.cs-rail-context` from the studio
 * courses prototype (Course Studio.html L143-177):
 * - Centered text alignment
 * - 156×156 cover (or skeleton) with the red diagonal-stripes empty state
 * - "Your course" eyebrow (10.5px / weight-600 / letter-spacing .04em)
 * - Title (15px / weight-700 / break-words / line-height 1.25)
 * - Meta row (status pill + lessons count) centered, 11px / text-tertiary
 * - Border-bottom separator
 */
function EntityHeader({ entity }: { entity: ContextualSidebarShellProps['entity'] }) {
  const aspectRatio = entity.thumbnailAspectRatio ?? '1/1';
  return (
    <div className="px-3 pb-3 mb-2 border-b border-white/10">
      <div className="flex flex-col items-center text-center pt-1 pb-2">
        {entity.thumbnail === undefined && entity.title === undefined ? (
          <Skeleton
            className={cn(
              'w-[156px] rounded-sm mb-3',
              aspectRatio === '16/9' ? 'h-[88px]' : 'h-[156px]'
            )}
          />
        ) : (
          <EmptyCoverThumb
            size={156}
            aspectRatio={aspectRatio}
            thumbnailUrl={entity.thumbnail}
            alt={entity.title ?? ''}
            rounded="sm"
            className="mb-3"
          />
        )}

        <div className="text-[10.5px] font-semibold text-text-tertiary tracking-[0.04em] mb-1">
          {entity.eyebrow ?? 'Your course'}
        </div>

        {entity.title ? (
          <p
            className="text-[15px] font-bold text-text-primary leading-tight tracking-tight mb-2.5 break-words w-full"
            title={entity.title}
          >
            {entity.title}
          </p>
        ) : (
          <Skeleton className="h-4 w-32 mb-2.5" />
        )}

        {entity.statusPill || entity.metaLine ? (
          <div className="flex items-center justify-center gap-2 flex-wrap text-[11px] text-text-tertiary">
            {entity.statusPill ?? null}
            {entity.metaLine ? <span>{entity.metaLine}</span> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
